import {
  type ReportInvestmentFundsModel,
  reportInvestmentFundCsvHeader,
} from '@/features/mold/models/investment-fund';
import { getProjectDetailPageUrl } from '@/features/project/_utils/project';
import { BusinessFormLabelModifier } from '@/features/project/projects/_models/project';
import { t } from '@/i18n';
import {
  createCsv,
  formatAmountForCsv,
  formatAmountNumberByUnit,
} from '@/utils';
import type { z } from 'zod';
import type { projectDetailUrlTypeEnum } from '../../models';
import { fundBusinessValueConverter } from './converter';

export const getInvestmentReportCsvHeader: (
  last3FiscalYears: number[],
) => Record<z.infer<typeof reportInvestmentFundCsvHeader>, string> = (
  last3FiscalYears,
) => ({
  fundSheetNumber: t('report-investment-fund-header-fund-sheet-number'),
  ministryName: t('ministry'),
  fundOwner: t('report-investment-fund-header-fund-owner'),
  projectName: t('fund-name'),
  startYear: t('report-investment-fund-header-start-year'),
  endYear: t('report-investment-fund-header-end-year'),
  accumulatedInvestmentsFromTheGovernment: t(
    'report-investment-fund-header-investment-from-goverment',
  ),
  investmentAmount2YearsBefore: t(
    'report-investment-fund-header-investment-amount',
    {
      year: last3FiscalYears[0],
    },
  ),
  investmentAmount1YearBefore: t(
    'report-investment-fund-header-investment-amount',
    {
      year: last3FiscalYears[1],
    },
  ),
  investmentAmountCurrentYear: t(
    'report-investment-fund-header-investment-amount',
    {
      year: last3FiscalYears[2],
    },
  ),
  accumulatedInvestmentFromPrivateSectors: t(
    'report-investment-fund-header-investment-from-private-sector',
  ),
  businessForm: t('report-investment-fund-header-business-form'),
  investmentDecision2YearsBeforeCount: `${t(
    'report-investment-fund-header-investment-amount',
    {
      year: last3FiscalYears[0],
    },
  )}${t('report-investment-fund-header-investment-decision-count')}`,
  investmentDecision2YearsBeforeAmount: `${t(
    'report-investment-fund-header-investment-amount',
    {
      year: last3FiscalYears[0],
    },
  )}${t('report-investment-fund-header-investment-decision-amount')}`,
  investmentExecution2YearsBeforeCount: `${t(
    'report-investment-fund-header-investment-amount',
    {
      year: last3FiscalYears[0],
    },
  )}${t('report-investment-fund-header-investment-execution-count')}`,
  investmentExecution2YearsBeforeAmount: `${t(
    'report-investment-fund-header-investment-amount',
    {
      year: last3FiscalYears[0],
    },
  )}${t('report-investment-fund-header-investment-execution-amount')}`,
  investmentDecision1YearBeforeCount: `${t(
    'report-investment-fund-header-investment-amount',
    {
      year: last3FiscalYears[1],
    },
  )}${t('report-investment-fund-header-investment-decision-count')}`,
  investmentDecision1YearBeforeAmount: `${t(
    'report-investment-fund-header-investment-amount',
    {
      year: last3FiscalYears[1],
    },
  )}${t('report-investment-fund-header-investment-decision-amount')}`,
  investmentExecution1YearBeforeCount: `${t(
    'report-investment-fund-header-investment-amount',
    {
      year: last3FiscalYears[1],
    },
  )}${t('report-investment-fund-header-investment-execution-count')}`,
  investmentExecution1YearBeforeAmount: `${t(
    'report-investment-fund-header-investment-amount',
    {
      year: last3FiscalYears[1],
    },
  )}${t('report-investment-fund-header-investment-execution-amount')}`,
  investmentDecisionCurrentYearCount: `${t(
    'report-investment-fund-header-investment-amount',
    {
      year: last3FiscalYears[2],
    },
  )}（${t('estimate')}）${t(
    'report-investment-fund-header-investment-decision-count',
  )}`,
  investmentDecisionCurrentYearAmount: `${t(
    'report-investment-fund-header-investment-amount',
    {
      year: last3FiscalYears[2],
    },
  )}（${t('estimate')}）${t(
    'report-investment-fund-header-investment-decision-amount',
  )}`,
  investmentExecutionCurrentYearCount: `${t(
    'report-investment-fund-header-investment-amount',
    {
      year: last3FiscalYears[2],
    },
  )}（${t('estimate')}）${t(
    'report-investment-fund-header-investment-execution-count',
  )}`,
  investmentExecutionCurrentYearAmount: `${t(
    'report-investment-fund-header-investment-amount',
    {
      year: last3FiscalYears[2],
    },
  )}（${t('estimate')}）${t(
    'report-investment-fund-header-investment-execution-amount',
  )}`,
  overview: t('project-overview'),
  projectDetailPageUrl: t('project-detail-page-url'),
});

const getInvestmentFundReportCsvBody = (
  report: z.infer<typeof ReportInvestmentFundsModel>['data'][number],
  isDebugMode: boolean,
  projectDetailUrlType: z.infer<typeof projectDetailUrlTypeEnum>,
): Record<
  z.infer<typeof reportInvestmentFundCsvHeader>,
  string | number | null
>[] => {
  const projectDetailPageUrl = getProjectDetailPageUrl({
    isPS: projectDetailUrlType === 'public',
    projectId: report.id,
  });
  if (report.fundBusinesses.length) {
    return report.fundBusinesses.map((business) => {
      const value = fundBusinessValueConverter({
        fundBusiness: business,
        type: 'csv',
        isDebugMode,
      });
      return {
        fundSheetNumber: `${report.fundSheetNumber}${
          report.fundSheetBranchNumber ? `-${report.fundSheetBranchNumber}` : ''
        }`,
        ministryName: report.ministryName,
        fundOwner: report.fundOwner,
        projectName: report.projectName,
        startYear: report.startYear ?? t('unknown-start'),
        endYear: report.endYear ?? t('no-scheduled-end'),
        accumulatedInvestmentsFromTheGovernment:
          typeof report.accumulatedInvestmentsFromTheGovernment === 'number'
            ? `${formatAmountForCsv(
                report.accumulatedInvestmentsFromTheGovernment,
                {
                  isDebugMode,
                  negativeCount:
                    report.negativeAccumulatedInvestmentsFromTheGovernmentCount,
                },
              )}${
                report
                  .accumulatedInvestmentsFromTheGovernmentAccountingTypeNames
                  .length > 0
                  ? `（${report.accumulatedInvestmentsFromTheGovernmentAccountingTypeNames.join(
                      '、',
                    )}）`
                  : ''
              }`
            : null,
        investmentAmount2YearsBefore:
          typeof report.investmentAmount2YearsBefore === 'number'
            ? formatAmountNumberByUnit(
                report.investmentAmount2YearsBefore,
                'thousand',
              )
            : null,
        investmentAmount1YearBefore:
          typeof report.investmentAmount1YearBefore === 'number'
            ? formatAmountNumberByUnit(
                report.investmentAmount1YearBefore,
                'thousand',
              )
            : null,
        investmentAmountCurrentYear:
          typeof report.investmentAmountCurrentYear === 'number'
            ? formatAmountNumberByUnit(
                report.investmentAmountCurrentYear,
                'thousand',
              )
            : null,
        accumulatedInvestmentFromPrivateSectors:
          typeof report.accumulatedInvestmentFromPrivateSectors === 'number'
            ? formatAmountNumberByUnit(
                report.accumulatedInvestmentFromPrivateSectors,
                'thousand',
              )
            : null,
        businessForm: BusinessFormLabelModifier[business.businessForm] ?? '',
        investmentDecision2YearsBeforeCount:
          value.investmentDecisionCount2YearsBefore,
        investmentDecision2YearsBeforeAmount:
          value.investmentDecisionAmount2YearsBefore,
        investmentExecution2YearsBeforeCount:
          value.investmentExecutionCount2YearsBefore,
        investmentExecution2YearsBeforeAmount:
          value.investmentExecutionAmount2YearsBefore,
        investmentDecision1YearBeforeCount:
          value.investmentDecisionCount1YearBefore,
        investmentDecision1YearBeforeAmount:
          value.investmentDecisionAmount1YearBefore,
        investmentExecution1YearBeforeCount:
          value.investmentExecutionCount1YearBefore,
        investmentExecution1YearBeforeAmount:
          value.investmentExecutionAmount1YearBefore,
        investmentDecisionCurrentYearCount:
          value.investmentDecisionCountCurrentYear,
        investmentDecisionCurrentYearAmount:
          value.investmentDecisionAmountCurrentYear,
        investmentExecutionCurrentYearCount:
          value.investmentExecutionCountCurrentYear,
        investmentExecutionCurrentYearAmount:
          value.investmentExecutionAmountCurrentYear,
        overview: report.overview,
        projectDetailPageUrl,
      };
    });
  }

  return [
    {
      fundSheetNumber: `${report.fundSheetNumber}${
        report.fundSheetBranchNumber ? `-${report.fundSheetBranchNumber}` : ''
      }`,
      ministryName: report.ministryName,
      fundOwner: report.fundOwner,
      projectName: report.projectName,
      startYear: report.startYear ?? t('unknown-start'),
      endYear: report.endYear ?? t('no-scheduled-end'),
      accumulatedInvestmentsFromTheGovernment:
        typeof report.accumulatedInvestmentsFromTheGovernment === 'number'
          ? formatAmountNumberByUnit(
              report.accumulatedInvestmentsFromTheGovernment,
              'thousand',
            )
          : null,
      investmentAmount2YearsBefore:
        typeof report.investmentAmount2YearsBefore === 'number'
          ? formatAmountNumberByUnit(
              report.investmentAmount2YearsBefore,
              'thousand',
            )
          : null,
      investmentAmount1YearBefore:
        typeof report.investmentAmount1YearBefore === 'number'
          ? formatAmountNumberByUnit(
              report.investmentAmount1YearBefore,
              'thousand',
            )
          : null,
      investmentAmountCurrentYear:
        typeof report.investmentAmountCurrentYear === 'number'
          ? formatAmountNumberByUnit(
              report.investmentAmountCurrentYear,
              'thousand',
            )
          : null,
      accumulatedInvestmentFromPrivateSectors:
        typeof report.accumulatedInvestmentFromPrivateSectors === 'number'
          ? formatAmountNumberByUnit(
              report.accumulatedInvestmentFromPrivateSectors,
              'thousand',
            )
          : null,
      businessForm: '',
      investmentDecision2YearsBeforeCount: null,
      investmentDecision2YearsBeforeAmount: null,
      investmentExecution2YearsBeforeCount: null,
      investmentExecution2YearsBeforeAmount: null,
      investmentDecision1YearBeforeCount: null,
      investmentDecision1YearBeforeAmount: null,
      investmentExecution1YearBeforeCount: null,
      investmentExecution1YearBeforeAmount: null,
      investmentDecisionCurrentYearCount: null,
      investmentDecisionCurrentYearAmount: null,
      investmentExecutionCurrentYearCount: null,
      investmentExecutionCurrentYearAmount: null,
      overview: report.overview,
      projectDetailPageUrl,
    },
  ];
};

export const convertInvestmentFundReportToCsv = ({
  headers,
  reports,
  isDebugMode,
  projectDetailUrlType,
}: {
  headers: Record<z.infer<typeof reportInvestmentFundCsvHeader>, string>;
  reports: z.infer<typeof ReportInvestmentFundsModel>;
  projectDetailUrlType: z.infer<typeof projectDetailUrlTypeEnum>;
  isDebugMode: boolean;
}) => {
  const csvData = createCsv({
    headerKeys: reportInvestmentFundCsvHeader.options,
    headerConverter: headers,
    body: reports.data.flatMap((report) =>
      getInvestmentFundReportCsvBody(report, isDebugMode, projectDetailUrlType),
    ),
  });

  return csvData;
};
